/**
 * L2 mode switch — `ModeSwitch`, the ONE component that swaps which BODY the
 * page is showing.
 *
 * It used to be two. One was a sticky pill tab bar over one scrolling body;
 * the other was "one exclusive MODE over ≥2 body regions". They were described
 * as different ideas — a tab re-filters one body, a mode replaces it — but they
 * were the SAME MECHANISM to the last line: an exclusive selection over a list
 * of options, routing children by `region`, with a region-less child as the
 * shared fallback body. The difference authors actually see is the DRAWING (a
 * row of pills that scrolls · a grouped track that fills its width), so the
 * drawing became a VARIANT (`pill` · `segmented`) and the two entries one.
 *
 * SEALED (both variants): the switch is NON-NETWORK — nothing is fetched, the
 * bodies are already there — and exactly one option is active.
 *
 * `preserveScroll` defaults ON for `pill` and OFF for `segmented`, because
 * that is what the two shapes promise. A pill bar sits above ONE long scrolling
 * list per tab and coming back to a tab you scrolled must land where you left
 * it; a segmented control REPLACES the body with a different one (a calendar
 * for a list), and there is no shared position to restore.
 *
 * The one thing this component may never do is decide what the switch MEANS.
 * It emits `modeChanged {key}` and the blueprint's connection decides whether
 * that re-filters a grid or swaps a page region — emitters stay generic, slots
 * stay typed (README §5).
 *
 * ONE name per port. The merge that produced this component carried a second
 * spelling of both (`tabChanged`, `setActive`) so a page written against either
 * retired shape kept working; that migration is over, and README §6.3 says an
 * alias table which outlives its migration is just two names for one thing.
 *
 * Four props widen WHICH tab is live and how it reads, without touching that
 * contract: `select` (a bound opening destination), `options[].when` (dim, do
 * not hide), `props.defaultFirstEnabled` (open on the first live one) and a
 * bound `options[].badge` (the unread dot). Each is documented at its use.
 */
import { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DynamicIcon } from 'lucide-react/dynamic';
import { resetButton, hitTarget, useSelection } from './ui';
import { evalWhen } from '../paths';
import { contentPadding } from '../layout';
import { isLucideIconName, lucideIconName } from './lucideIcons';
import { TabBarHostContext, optionsOf, tabBarFits, tabCountLabel } from './tabBarHost';

/** The bottom section tab bar (the board page's look), drawn with kit tokens. */
function BottomTabBar({ options, active, enabled, onPick }) {
  return (
    <div
      role="tablist"
      data-testid="mode-tabbar"
      style={{
        display: 'flex', background: 'var(--bg)', borderTop: '1px solid var(--line)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {options.map((o) => {
        const on = o.key === active;
        const live = enabled(o);
        const label = o.label || o.key;
        const count = tabCountLabel(o.count);
        const color = on ? 'var(--text)' : 'var(--text-3)';
        return (
          <button
            key={o.key}
            type="button"
            role="tab"
            aria-selected={on}
            aria-disabled={live ? undefined : true}
            aria-label={count ? `${label}, ${count}` : label}
            data-tab={o.key}
            onClick={() => (live ? onPick(o.key) : undefined)}
            style={{
              ...resetButton,
              flex: '1 1 0', minWidth: 0, height: 56, padding: '6px 4px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              color, opacity: live ? 1 : 0.4, cursor: live ? 'pointer' : 'default',
            }}
          >
            <span style={{ position: 'relative', width: 22, height: 22, flex: '0 0 auto' }}>
              <DynamicIcon
                name={isLucideIconName(o.icon) ? lucideIconName(o.icon) : 'circle'}
                size={22} color="currentColor" strokeWidth={1.75} aria-hidden
              />
              {count ? (
                <span data-testid="mode-tabbar-count" aria-hidden style={{
                  position: 'absolute', left: 14, top: -5, height: 16, minWidth: 16, padding: '0 4px',
                  boxSizing: 'border-box', borderRadius: 'var(--r-pill)', background: 'var(--bad)',
                  color: '#fff', fontSize: 10, lineHeight: '16px', fontWeight: 700, textAlign: 'center',
                }}>{count}</span>
              ) : null}
              {!count && o.badge ? (
                <span aria-hidden style={{
                  position: 'absolute', right: -3, top: -1, width: 7, height: 7, boxSizing: 'border-box',
                  borderRadius: 'var(--r-pill)', background: 'var(--accent)', border: '1px solid var(--accent-ink)',
                }} />
              ) : null}
            </span>
            <span style={{
              maxWidth: '100%', fontSize: 12, lineHeight: 1.1, fontWeight: on ? 600 : 500,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * An option's `when` DIMS it; it never hides it.
 *
 * A tab bar is a map of the widget: "Photos" being absent because you have no
 * photos yet reads as a feature that does not exist, and the map changes shape
 * under the user between two visits. Greyed-out says the same thing honestly —
 * this destination is here and there is nothing in it yet.
 */
function enabledOption(o, ctx) {
  return o?.when === undefined ? true : Boolean(evalWhen(o.when, ctx));
}

/** 导航条 barPadding 与 tab 正文独立，保留原有切换和滚动状态归属。 */
export function ModeSwitch({ nodeId, variant, props = {}, emit, slots, childItems = [], children, ctx }) {
  const host = useContext(TabBarHostContext);
  const options = optionsOf(props);
  // Only the PageShell that claimed this node pins it; anywhere else a tabbar draws segmented.
  const shape = props.variant || variant || 'segmented';
  const tabbar = shape === 'tabbar' && host?.tabbarId === nodeId && tabBarFits(options.length);
  const pill = shape === 'pill';
  const enabled = (o) => enabledOption(o, ctx);
  /**
   * WHICH TAB THE PAGE OPENS ON, in the order that answers it:
   *   `select`  a BOUND seed — `{"$": "page_args.tab"}`. The host opens a page
   *             on a destination ("the new one landed in Creations"), and the
   *             switch has to be looking at it when the page draws, not one
   *             tap later. A seed naming no option is ignored rather than
   *             leaving the bar with nothing lit.
   *   `page_args.tab`  the same answer when the blueprint did not declare one.
   *             The host already speaks this: a capture carries `target_tab`
   *             and the page it lands on gets it as `page_args.tab`, so a
   *             switch that ignored it dropped the user on the tab they were
   *             NOT waiting for. Undeclared, both runtimes read it.
   *   `defaultFirstEnabled`  the first option whose `when` passes, so a page
   *             does not open on a dimmed tab holding nothing.
   *   otherwise the first option, which is what it has always been.
   */
  const declaredSeed = props.select == null || props.select === '' ? null : String(props.select);
  const hostSeed = ctx?.page_args?.tab == null ? null : String(ctx.page_args.tab);
  const seed = declaredSeed || hostSeed;
  const seeded = seed && options.some((o) => String(o.key) === seed) ? seed : null;
  const firstEnabled = props.defaultFirstEnabled
    ? (options.find(enabled)?.key ?? options[0]?.key ?? null)
    : (options[0]?.key ?? null);
  const [active, setActive] = useSelection(nodeId, seeded ?? firstEnabled);
  const scrollTops = useRef({});
  const bodyRef = useRef(null);
  const pushed = slots?.setMode;
  const preserveScroll = props.preserveScroll === undefined ? (pill || tabbar) : props.preserveScroll !== false;

  useEffect(() => {
    const want = pushed?.payload?.key !== undefined ? pushed.payload.key : pushed?.payload?.tab;
    if (want !== undefined) setActive(want);
  }, [pushed, setActive]);

  const pick = (key) => {
    if (key === active) return;
    if (!enabled(options.find((o) => o.key === key))) return;
    if (tabbar) {
      // The page body is the scroller: land at the top unless this tab kept an offset.
      const scroller = host.scroller?.();
      if (preserveScroll && scroller) scrollTops.current[active] = scroller.scrollTop;
      setActive(key);
      emit?.('modeChanged', { key });
      requestAnimationFrame(() => {
        const el = host.scroller?.();
        if (el) el.scrollTop = preserveScroll ? (scrollTops.current[key] || 0) : 0;
      });
      return;
    }
    if (preserveScroll && bodyRef.current) scrollTops.current[active] = bodyRef.current.scrollTop;
    setActive(key);
    emit?.('modeChanged', { key });
    if (!preserveScroll) return;
    requestAnimationFrame(() => {
      if (bodyRef.current) bodyRef.current.scrollTop = scrollTops.current[key] || 0;
    });
  };

  const claimed = new Set(options.map((o) => o.key));
  // A child whose `region` IS the active key is that mode's body; a child with
  // no region — or a region no option claims — is the SHARED body the other
  // modes fall back to (one grid serving five category tabs, re-filtered by
  // the tab's own connection). `pending` is reserved, never a body.
  const body = childItems.find((c) => c.region === active)
    || childItems.find((c) => !c.region || (!claimed.has(c.region) && c.region !== 'pending'));
  const pendingChild = pill || tabbar ? childItems.find((c) => c.region === 'pending') : null;
  // Pending adds carry their own `tab`; a tab-less add falls back to the tab
  // the page landed on — never a skeleton on every tab.
  const pendingAdds = pendingChild
    ? (ctx?.pending?.adds || []).filter(
      (a) => !a.tab || a.tab === active || (active === options[0]?.key && !a.tab),
    )
    : [];

  const track = pill
    ? {
      display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none',
      position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg)', ...contentPadding(props.contentPadding), paddingBottom: contentPadding(props.contentPadding).paddingBottom + 2,
    }
    : {
      display: 'flex', gap: 2, padding: 2, borderRadius: 'var(--r-pill)',
      background: 'var(--surface-3)', alignSelf: props.block === false ? 'flex-start' : 'stretch',
    };

  const buttonStyle = (on) => (pill
    ? {
      ...resetButton, ...hitTarget,
      flex: '0 0 auto', minWidth: 0, minHeight: 34,
      padding: '7px 14px', borderRadius: 'var(--r-pill)',
      fontSize: 'var(--t-sm)', fontWeight: on ? 700 : 500,
      background: on ? 'var(--accent)' : 'var(--surface)',
      color: on ? 'var(--accent-ink)' : 'var(--text-2)',
      border: `1px solid ${on ? 'var(--accent)' : 'var(--line)'}`,
      whiteSpace: 'nowrap',
    }
    : {
      ...resetButton,
      flex: 1, minWidth: 0, minHeight: 34, padding: '6px 12px',
      borderRadius: 'var(--r-pill)', textAlign: 'center',
      fontSize: 'var(--t-sm)', fontWeight: on ? 700 : 500,
      background: on ? 'var(--surface)' : 'transparent',
      color: on ? 'var(--text)' : 'var(--text-3)',
      boxShadow: on ? 'var(--shadow-1)' : 'none',
      transition: 'background 140ms var(--ease)',
    });

  const bar = (
    <div role={pill ? undefined : 'group'} aria-label={pill ? undefined : (props.label || 'Mode')} style={track}>
      {options.map((o) => {
        const on = o.key === active;
        const live = enabled(o);
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={on}
            aria-disabled={live ? undefined : true}
            onClick={() => (live ? pick(o.key) : undefined)}
            style={{
              ...buttonStyle(on),
              position: 'relative',
              opacity: live ? 1 : 0.4,
              cursor: live ? 'pointer' : 'default',
            }}
          >
            {o.label || o.key}
            {/* The UNREAD DOT, and it is BOUND: `badge: {"$": …}` resolves like
                any other option field, so the dot appears because something is
                actually waiting on that destination rather than because the
                blueprint hard-coded a dot that never goes away. Both shapes
                draw it — a pill bar has the same "something landed over there"
                to say as a segmented one. */}
            {o.badge ? (
              <span aria-hidden style={{
                position: 'absolute', top: 6, right: 6, width: 7, height: 7,
                borderRadius: 'var(--r-pill)', background: 'var(--accent)',
                border: '1px solid var(--accent-ink)',
              }} />
            ) : null}
          </button>
        );
      })}
    </div>
  );

  if (tabbar) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', minWidth: 0 }}>
        {pendingChild && pendingAdds.length > 0 ? pendingChild.element : null}
        {body?.element || null}
        {host.target ? createPortal(
          <BottomTabBar options={options} active={active} enabled={enabled} onPick={pick} />,
          host.target,
        ) : null}
      </div>
    );
  }

  // A pill bar owns the SCROLLER (that is what per-tab scroll preservation
  // means), so it fills its parent; a segmented control leaves the body's
  // scrolling to whatever is around it.
  if (pill) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, gap: 'var(--sp-3)' }}>
        {props.barPadding ? <div style={contentPadding(props.barPadding)}>{bar}</div> : bar}
        <div ref={bodyRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }}>
          {pendingChild && pendingAdds.length > 0 ? (
            <div style={{ marginBottom: 'var(--sp-3)' }}>{pendingChild.element}</div>
          ) : null}
          {body?.element || null}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)', minWidth: 0 }}>
      {props.barPadding ? <div style={contentPadding(props.barPadding)}>{bar}</div> : bar}
      {childItems.length ? (body?.element || null) : children}
    </div>
  );
}
